import { useEffect, useState } from 'react';
import { X, TrendingUp } from 'lucide-react';
import { trackMonetizationCtaImpression, trackMonetizationCtaClick } from '@/lib/analytics';

// MON-014: Tata Price Hike Countdown Banner
// Tata Motors confirmed up to 1.5% price increase across all PV models (ICE + EV)
// effective July 1, 2026. Self-sunsets after deadline.

const PRICE_HIKE_DATE = new Date(Date.UTC(2026, 6, 1, 0, 0)); // July 1, 2026
const DISMISS_KEY = 'mw_mon014_dismissed';

export function daysUntilPriceHike(): number {
  const diff = PRICE_HIKE_DATE.getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export function formatPriceHikeLabel(daysLeft: number): string {
  if (daysLeft <= 0) return 'Tata prices have increased';
  if (daysLeft === 1) return 'Tata prices rise tomorrow — last chance to save';
  return `Tata prices rise July 1 — ${daysLeft} days left`;
}

export function TataPriceHikeCountdown() {
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1'
  );
  const [daysLeft, setDaysLeft] = useState(() => daysUntilPriceHike());
  const [impressionFired, setImpressionFired] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setDaysLeft(daysUntilPriceHike()), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!dismissed && !impressionFired && daysLeft > 0) {
      trackMonetizationCtaImpression('price_hike_banner', 'tata-price-hike', 'four-wheeler', 'Tata Motors', undefined, 'announcement_banner');
      setImpressionFired(true);
    }
  }, [dismissed, impressionFired, daysLeft]);

  if (dismissed || daysLeft <= 0) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  const handleCtaClick = () => {
    trackMonetizationCtaClick('price_hike_banner', 'tata-price-hike', 'four-wheeler', 'Tata Motors', '/guides/tata-price-hike-july-2026', undefined, 'announcement_banner');
  };

  return (
    <div className="relative flex items-center justify-center gap-2 bg-gradient-to-r from-red-500/15 via-orange-500/10 to-red-500/15 border-b border-red-500/20 px-4 py-2 text-sm">
      <TrendingUp className="h-4 w-4 text-red-400 flex-shrink-0" />
      <span className="text-red-200">
        <strong>{formatPriceHikeLabel(daysLeft)}</strong> — Save up to ₹37,500 on Tata vehicles.
      </span>
      <a
        href="/guides/tata-price-hike-july-2026"
        onClick={handleCtaClick}
        className="font-semibold text-red-400 hover:text-red-300 underline underline-offset-2 whitespace-nowrap"
      >
        Get best price now →
      </a>
      <button
        onClick={handleDismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
